import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, ArrowLeft, Paintbrush, Palette, ClipboardList, Users } from 'lucide-react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import SEO from '@/components/shared/SEO';
import ConsultationIntakeModal from '@/components/modals/ConsultationIntakeModal';

// Services
const consultationServices = [
  {
    id: 'material-selection',
    title: 'Material Selection',
    description: 'Expert guidance on choosing the right flooring materials for your specific environment, traffic patterns, and performance needs.',
    icon: Palette,
    includes: ['Performance analysis', 'Durability assessment', 'Cost comparison', 'Lifecycle planning']
  },
  {
    id: 'branding-integration',
    title: 'Branding Integration',
    description: 'Strategic integration of your brand identity into flooring design, from color matching to custom patterns.',
    icon: Paintbrush,
    includes: ['Color matching', 'Pattern development', 'Logo placement', 'Brand guidelines adherence']
  },
  {
    id: 'space-planning',
    title: 'Space Planning',
    description: 'Comprehensive space analysis to optimize flooring zones, transitions, and visual flow.',
    icon: ClipboardList,
    includes: ['Zone mapping', 'Traffic flow analysis', 'Transition planning', 'ADA compliance']
  },
  {
    id: 'project-consulting',
    title: 'Project Consulting',
    description: 'End-to-end project guidance from initial concept through installation and maintenance planning.',
    icon: Users,
    includes: ['Budget development', 'Timeline planning', 'Vendor coordination', 'Quality assurance']
  }
];

// Process Steps
const processSteps = [
  {
    step: 1,
    title: 'Discovery Call',
    description: 'Initial conversation to understand your project scope, goals, and timeline.',
    duration: '30-60 min'
  },
  {
    step: 2,
    title: 'Site Assessment',
    description: 'On-site evaluation of existing conditions, measurements, and requirements.',
    duration: '1-2 hours'
  },
  {
    step: 3,
    title: 'Concept Development',
    description: 'Design concepts and material recommendations tailored to your needs.',
    duration: '3-5 days'
  },
  {
    step: 4,
    title: 'Presentation & Review',
    description: 'Detailed presentation of options with samples, renderings, and specifications.',
    duration: '1-2 hours'
  },
  {
    step: 5,
    title: 'Refinement',
    description: 'Incorporate feedback and finalize design direction.',
    duration: 'As needed'
  },
  {
    step: 6,
    title: 'Final Documentation',
    description: 'Complete specifications, drawings, and project documentation.',
    duration: '5-7 days'
  }
];

// Why Us Points
const whyChooseUs = [
  {
    title: '25+ Years Experience',
    description: 'Deep expertise in commercial flooring across all industries'
  },
  {
    title: 'In-House Design Team',
    description: 'Dedicated designers focused on commercial flooring solutions'
  },
  {
    title: 'Manufacturer Relationships',
    description: 'Direct access to leading flooring manufacturers and materials'
  },
  {
    title: 'Full-Service Capability',
    description: 'Design through installation under one roof'
  }
];

const CustomConsultationPage = () => {
  const [isIntakeOpen, setIsIntakeOpen] = useState(false);
  return (
    <Layout>
      <SEO 
        title="Free Flooring Consultation"
        description="Schedule a free consultation with our flooring experts. Get personalized recommendations for material selection, branding integration, and space planning tailored to your business."
        canonicalUrl="/custom/consultation"
      />
      {/* Hero Section */}
      <section className="relative bg-primary-bg text-white py-20" data-testid="consultation-hero">
        <div className="container-portico">
          <Link to="/custom" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Custom Hub
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Design & Consultation
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Expert guidance from concept to completion. Our design team helps you navigate 
              material selection, branding integration, and performance requirements.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg" 
                className="bg-secondary hover:bg-secondary-light"
                onClick={() => setIsIntakeOpen(true)}
                data-testid="hero-schedule-consultation"
              >
                Schedule Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Link to="/custom/portfolio">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
                  View Our Work
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding bg-background">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Consultation Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive design and consulting services to ensure your flooring project exceeds expectations.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {consultationServices.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-xl p-8 shadow-lg border border-border hover:shadow-xl transition-shadow"
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-heading font-bold text-xl mb-3">{service.title}</h3>
                <p className="text-muted-foreground mb-6">{service.description}</p>
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Includes:</h4>
                  <ul className="grid grid-cols-2 gap-2">
                    {service.includes.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-secondary flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-padding bg-muted">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Our Consultation Process
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A structured approach that respects your time while delivering comprehensive solutions.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-6 rounded-xl border border-border"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold">
                    {step.step}
                  </div>
                  <span className="text-sm text-muted-foreground">{step.duration}</span>
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-background">
        <div className="container-portico">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Why Work With Our Design Team?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Our in-house design team brings decades of commercial flooring expertise to every project. 
                We don't just recommend products—we craft complete solutions.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {whyChooseUs.map((point, index) => (
                  <motion.div
                    key={point.title}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-semibold mb-1">{point.title}</h3>
                        <p className="text-sm text-muted-foreground">{point.description}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="/Assets/images/custom/design-team.jpg"
                alt="Design consultation"
                className="rounded-xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="section-padding bg-primary-light text-white">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              What to Expect
            </h2>
            <p className="text-lg text-white/90 mb-8">
              When you schedule a consultation with our team, you'll receive:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <h3 className="font-semibold mb-2">Personalized Attention</h3>
                <p className="text-sm text-white/80">
                  A dedicated consultant focused on understanding your unique requirements.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <h3 className="font-semibold mb-2">Expert Recommendations</h3>
                <p className="text-sm text-white/80">
                  Product and design recommendations based on your specific needs.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <h3 className="font-semibold mb-2">No Obligation</h3>
                <p className="text-sm text-white/80">
                  Initial consultations are complimentary with no commitment required.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="schedule-section" className="section-padding bg-muted">
        <div className="container-portico text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Schedule Your Consultation
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Ready to discuss your project? Our design team is here to help 
              turn your vision into reality.
            </p>
            <Button size="lg" className="bg-primary hover:bg-primary/90" onClick={() => setIsIntakeOpen(true)} data-testid="schedule-consultation-cta">
              Schedule Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      <ConsultationIntakeModal
        isOpen={isIntakeOpen}
        onClose={() => setIsIntakeOpen(false)}
      />
    </Layout>
  );
};

export default CustomConsultationPage;
